<template>
  <div
    class="draggable-image-container"
    :class="{ 'is-dragging': isDragging, 'draggable': $store.state.auth.userData.role === 'admin' }"
  >
    <div
      v-for="(img, index) in images"
      :key="`draggable-image-${index}`"
      ref="imageElements"
      :class="[
        'image-item mr-50 mb-50',
        { 'is-selected': isDragging && draggedIndex === index }
      ]"
      @mousedown="startDrag($event, index)"
    >
      <ShowImage
        :src="img.image_url"
        :index="index"
        :is-main="!isDragging && index === 0"
        :action="!isDragging"
        @setmain="$emit('setmain', index)"
        @delete="$emit('delete', index)"
      />
    </div>
    <slot
      v-if="!isDragging"
      name="upload"
    />
  </div>
</template>

<script>
import ShowImage from '@/layouts/components/custom/ShowImage.vue'

export default {
  name: 'DraggableImageContainer',

  components: {
    ShowImage,
  },

  props: {
    images: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      isDragging: false,
      draggedIndex: null,
      currentPos: { x: 0, y: 0 },
      elements: [],
      elementsPos: [],
      previousOrder: [],
    }
  },

  watch: {
    images: {
      handler() {
        this.$nextTick(() => {
          this.calculateElementPositions()
        })
      },
      deep: true,
    },
  },

  mounted() {
    if (this.$store.state.auth.userData.role === 'admin') {
      window.addEventListener('mousemove', this.onMouseMove)
      window.addEventListener('mouseup', this.onMouseUp)

      this.$nextTick(() => {
        this.calculateElementPositions()
      })
    }
  },

  beforeDestroy() {
    if (this.$store.state.auth.userData.role === 'admin') {
      window.removeEventListener('mousemove', this.onMouseMove)
      window.removeEventListener('mouseup', this.onMouseUp)
    }
  },

  methods: {
    calculateElementPositions() {
      if (!this.$refs.imageElements) return

      this.elements = this.$refs.imageElements
      this.elementsPos = this.elements.map(el => {
        const rect = el.getBoundingClientRect()
        return {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
          centerX: rect.left + rect.width / 2,
          centerY: rect.top + rect.height / 2,
        }
      })
    },

    startDrag(event, index) {
      if (this.$store.state.auth.userData.role !== 'admin') {
        return
      }

      // Check if click originated from dropdown or its children
      let { target } = event
      while (target) {
        if (
          target.classList
          && (target.classList.contains('dropdown-menu'))
        ) {
          // Click came from dropdown or its items, don't start dragging
          return
        }
        // Also ignore clicks on icon elements
        if (target.tagName === 'svg' || target.tagName === 'FEATHER-ICON') {
          return
        }
        target = target.parentElement
      }

      // Prevent default browser drag behavior
      event.preventDefault()

      // Calculate element positions
      this.calculateElementPositions()

      this.isDragging = true
      this.draggedIndex = index
      this.currentPos = { x: event.clientX, y: event.clientY }

      // Emit drag start event to parent
      this.$emit('drag-start')
    },

    onMouseMove(event) {
      if (!this.isDragging) return

      this.currentPos = { x: event.clientX, y: event.clientY }

      // Check if hovering over another image
      const hoverIndex = this.getHoveredElementIndex()

      if (hoverIndex !== -1 && hoverIndex !== this.draggedIndex) {
        // Swap images
        this.$emit('reorder', this.draggedIndex, hoverIndex)

        // Update dragged index
        this.draggedIndex = hoverIndex

        // Recalculate positions after the swap
        this.calculateElementPositions()
      }
    },

    getHoveredElementIndex() {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < this.elementsPos.length; i++) {
        // eslint-disable-next-line no-continue
        if (i === this.draggedIndex) continue

        const pos = this.elementsPos[i]

        if (
          this.currentPos.x >= pos.x
          && this.currentPos.x <= pos.x + pos.width
          && this.currentPos.y >= pos.y
          && this.currentPos.y <= pos.y + pos.height
        ) {
          return i
        }
      }

      return -1
    },

    onMouseUp() {
      if (!this.isDragging) return

      this.isDragging = false
      this.draggedIndex = null
      this.previousOrder = []

      // Emit drag end event to parent
      this.$emit('drag-end')
    },
  },
}
</script>

<style lang="scss" scoped>
.draggable-image-container {
  position: relative;
  display: flex;
  flex-wrap: wrap;

  &.draggable.is-dragging {
    cursor: grabbing;
  }
}

.image-item {
  position: relative;
}

.draggable-image-container.draggable .image-item {
  cursor: grab;

  &.is-selected {
    z-index: 10;
    opacity: 0.5;
    transform: scale(0.9);
    cursor: grabbing;
  }
}
</style>
